// ---------------------------------------------------------------------------
// YouAreYou (uru) — Requests Page
//
// Protected page rendered at /requests. Shows pending inbound connection
// requests that the authenticated user can accept or deny.
//
// No mockup exists — derived from the app's established patterns
// (ConnectionsPage for list layout, ActivityPage for expandable detail,
// ConnectionDetailPage for action buttons).
//
// Data flow:
//   - GET /api/uru/connections?status=pending&sort=-created → pending connections
//   - POST /api/uru/connections/{pk} {"accept": true} → accept
//   - POST /api/uru/connections/{pk} {"deny": {"reason": "..."}} → deny
//
// The page distinguishes between inbound requests (where someone else is the
// requestor) and outbound requests (where the current user is the requestor).
// Direction is determined from connection.direction field ("outgoing"/"incoming").
//
// Features:
//   - Pending request count badge
//   - Search by contact name
//   - Expandable request cards with accept/deny actions
//   - Deny with optional reason
//   - Outbound requests show "Cancel" instead of accept/deny
//   - Loading skeleton, empty state, error with retry
//   - Toast-style feedback after actions
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  listConnections,
  acceptConnection,
  denyConnection,
  cancelConnection,
  getConnection,
  grantDataSharing,
  revokeDataSharing,
} from "@/api/endpoints";
import type { Connection, SharableField, SharingMode } from "@/types/api";
import { useAuth } from "@/hooks/useAuth";
import Icon from "@/components/ui/Icon";
import { cn, formatRelativeTime, getInitials, copyToClipboard } from "@/lib/utils";

// Rose-refresh "monolithic" card: borderless white with ambient shadow.
const CARD_SHADOW =
  "shadow-[0px_4px_20px_rgba(26,31,43,0.04)] hover:shadow-[0px_8px_32px_rgba(26,31,43,0.08)] transition-shadow";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EnrichedRequest extends Connection {
  displayName: string;
  initials: string;
  isOutbound: boolean;
  avatarUrl: string | null;
}

type ActionFeedback = {
  connectionId: number;
  action: "accepted" | "denied" | "cancelled";
} | null;

/** A pending data sharing request that I need to act on (grant or decline). */
interface PendingSharingRequest {
  connectionId: number;
  contactName: string;
  field: SharableField;
}

/** Returns true if the string looks like a raw phone number, not a user label. */
function isPhoneLikeLabel(s: string): boolean {
  const t = s.trim();
  return t.startsWith("+") || /^\d[\d\s\-().]*$/.test(t);
}

const FIELD_LABELS: Record<SharableField, string> = {
  phone: "Phone Number",
  email: "Email Address",
  address: "Home Address",
  ssn: "SSN Verification",
  gov_id: "Gov ID Data",
  liveness: "Liveness Check",
  ofac: "OFAC Check",
  adverse_media: "Adverse Media",
};

// ---------------------------------------------------------------------------
// Enrichment — determine the "other party" and direction
// ---------------------------------------------------------------------------

function enrichRequest(
  conn: Connection,
  _currentUserName: string | undefined,
): EnrichedRequest {
  // Direction is explicit — no name-matching needed
  const isOutbound = conn.direction === "outgoing";
  const other = conn.connected_customer;

  const firstName = other?.first_name || "Unknown";
  const lastName = other?.last_name || "";
  const displayName = other
    ? `${firstName} ${lastName}`.trim()
    : "Awaiting Recipient";

  return {
    ...conn,
    displayName,
    initials: getInitials(firstName, lastName),
    isOutbound,
    avatarUrl: other?.avatar || null,
  };
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6">
      {/* Header skeleton */}
      <div className="mb-6">
        <div className="h-7 w-48 animate-pulse rounded bg-gray-200" />
        <div className="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200" />
      </div>
      {/* Search skeleton */}
      <div className="mb-4 h-10 w-full animate-pulse rounded-lg bg-gray-200" />
      {/* Card skeletons */}
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-20 w-full animate-pulse rounded-xl bg-gray-100"
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  isSearching: boolean;
}

function EmptyState({ isSearching }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[#fff0f0]">
        <Icon
          name={isSearching ? "search_off" : "inbox"}
          className="text-4xl text-[#f08b8b]"
          label={isSearching ? "No results" : "No pending requests"}
        />
      </div>
      <h3 className="text-lg font-bold text-text-primary">
        {isSearching ? "No matching requests" : "No pending requests"}
      </h3>
      <p className="mt-2 max-w-xs text-sm text-text-secondary">
        {isSearching
          ? "Try a different search term."
          : "When someone sends you a connection request, it will appear here."}
      </p>
      {!isSearching && (
        <Link
          to="/dashboard"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600"
        >
          Share your trust link
          <Icon name="arrow_forward" className="text-lg" />
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State
// ---------------------------------------------------------------------------

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-error/10">
        <Icon
          name="cloud_off"
          className="text-4xl text-error"
          label="Error loading requests"
        />
      </div>
      <h3 className="text-lg font-bold text-text-primary">
        Couldn&apos;t load requests
      </h3>
      <p className="mt-2 max-w-xs text-sm text-text-secondary">{message}</p>
      <button
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
      >
        <Icon name="refresh" className="text-lg" />
        Try Again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CopyLinkButton
// ---------------------------------------------------------------------------

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void handleCopy(); }}
      className={cn(
        "flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/20",
        copied
          ? "bg-emerald-50 text-emerald-600"
          : "bg-gray-100 text-text-secondary hover:bg-primary-50 hover:text-primary-600",
      )}
      aria-label={copied ? "Copied" : "Copy link"}
    >
      <Icon name={copied ? "check" : "content_copy"} className="text-sm" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Request Card
// ---------------------------------------------------------------------------

interface RequestCardProps {
  request: EnrichedRequest;
  isExpanded: boolean;
  onToggle: () => void;
  onAccept: () => void;
  onDeny: (reason?: string) => void;
  onCancel: () => void;
  isActioning: boolean;
  userFirstName?: string;
  anonIndex?: number;
}

function RequestCard({
  request,
  isExpanded,
  onToggle,
  onAccept,
  onDeny,
  onCancel,
  isActioning,
  userFirstName,
  anonIndex,
}: RequestCardProps) {
  const [denyReason, setDenyReason] = useState("");
  const [showDenyForm, setShowDenyForm] = useState(false);

  const handleDenySubmit = useCallback(() => {
    onDeny(denyReason.trim() || undefined);
  }, [onDeny, denyReason]);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl bg-white",
        CARD_SHADOW,
        isExpanded && "ring-1 ring-[#f08b8b]/30",
      )}
    >
      {/* Collapsed row */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[#faf9ff]"
        aria-expanded={isExpanded}
      >
        {/* Avatar */}
        <div className="h-11 w-11 shrink-0 rounded-full overflow-hidden">
          {request.avatarUrl ? (
            <img
              src={request.avatarUrl}
              alt={request.displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600">
              {request.initials}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-text-primary">
            {request.isOutbound && request.source === "direct_link"
              ? ((request.label && !isPhoneLikeLabel(request.label) ? request.label : null) ||
                (request.connected_customer
                  ? `${request.connected_customer.first_name} ${request.connected_customer.last_name}`.trim()
                  : `${userFirstName ?? "My"}'s Trust link ${String(anonIndex ?? 1).padStart(3, "0")}`))
              : request.displayName}
          </p>
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            {request.isOutbound ? (
              <>
                <Icon name="call_made" className="text-xs text-primary-400" />
                <span>You sent this request</span>
              </>
            ) : (
              <>
                <Icon name="call_received" className="text-xs text-warning" />
                <span>Wants to connect with you</span>
              </>
            )}
            <span className="text-text-muted">·</span>
            <span>{formatRelativeTime(request.created)}</span>
          </p>
        </div>

        {/* Source badge */}
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
            request.source === "global_link"
              ? "bg-primary-50 text-primary-500"
              : "bg-orange-50 text-orange-600",
          )}
        >
          {request.source === "global_link" ? "Global" : "Direct"}
        </span>

        {/* Chevron */}
        <Icon
          name={isExpanded ? "expand_less" : "expand_more"}
          className="shrink-0 text-xl text-text-muted"
        />
      </button>

      {/* Link copy bar — always visible for outbound direct-link connections */}
      {request.isOutbound && request.source === "direct_link" && request.link && (
        <div className="mx-4 mb-3 flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2.5">
          <span className="flex-1 truncate text-sm font-medium text-text-secondary">
            {request.link.replace(/^https?:\/\//, "")}
          </span>
          <CopyLinkButton url={request.link} />
        </div>
      )}

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t border-[#f3ecec] bg-[#faf9ff] px-5 py-4">
          {/* Connection metadata */}
          <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="font-medium text-text-muted">Direction</span>
              <p className="mt-0.5 font-semibold text-text-primary">
                {request.isOutbound ? "Sent by you" : "Received"}
              </p>
            </div>
            <div>
              <span className="font-medium text-text-muted">Contact</span>
              <p className="mt-0.5 font-semibold text-text-primary">
                {request.connected_customer
                  ? `${request.connected_customer.first_name} ${request.connected_customer.last_name}`
                  : "Awaiting recipient"}
              </p>
            </div>
            <div>
              <span className="font-medium text-text-muted">Source</span>
              <p className="mt-0.5 font-semibold text-text-primary">
                {request.source === "global_link"
                  ? "Global Link"
                  : "Direct Link"}
              </p>
            </div>
            <div>
              <span className="font-medium text-text-muted">When</span>
              <p className="mt-0.5 font-semibold text-text-primary">
                {formatRelativeTime(request.created)}
              </p>
            </div>
          </div>

          {/* Deny reason form */}
          {showDenyForm && !request.isOutbound && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
              <label
                htmlFor={`deny-reason-${request.id}`}
                className="mb-1 block text-xs font-medium text-text-secondary"
              >
                Reason for declining{" "}
                <span className="text-text-muted">(optional)</span>
              </label>
              <input
                id={`deny-reason-${request.id}`}
                type="text"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="I don't know this person"
                disabled={isActioning}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:bg-gray-50"
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleDenySubmit}
                  disabled={isActioning}
                  className="flex items-center gap-1.5 rounded-lg bg-error px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                >
                  {isActioning ? (
                    <Icon
                      name="progress_activity"
                      className="animate-spin text-sm"
                    />
                  ) : (
                    <Icon name="close" className="text-sm" />
                  )}
                  Confirm Decline
                </button>
                <button
                  onClick={() => {
                    setShowDenyForm(false);
                    setDenyReason("");
                  }}
                  disabled={isActioning}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {request.isOutbound ? (
              // Outbound: the current user sent this — they can cancel
              <button
                onClick={onCancel}
                disabled={isActioning}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:border-error/30 hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isActioning ? (
                  <Icon
                    name="progress_activity"
                    className="animate-spin text-lg"
                  />
                ) : (
                  <Icon name="block" className="text-lg" />
                )}
                Cancel Request
              </button>
            ) : (
              // Inbound: someone sent this to the user — they can accept or deny
              <>
                <button
                  onClick={onAccept}
                  disabled={isActioning}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isActioning ? (
                    <Icon
                      name="progress_activity"
                      className="animate-spin text-lg"
                    />
                  ) : (
                    <Icon name="check" className="text-lg" />
                  )}
                  Accept
                </button>
                <button
                  onClick={() => setShowDenyForm(true)}
                  disabled={isActioning || showDenyForm}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:border-error/30 hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon name="close" className="text-lg" />
                  Decline
                </button>
              </>
            )}
          </div>

          {/* View detail link */}
          <Link
            to={`/connections/${request.id}`}
            className="mt-3 flex items-center justify-center gap-1 text-xs font-medium text-primary-500 transition-colors hover:text-primary-600"
          >
            View full details
            <Icon name="arrow_forward" className="text-sm" />
          </Link>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action Feedback Toast
// ---------------------------------------------------------------------------

interface FeedbackToastProps {
  feedback: ActionFeedback;
  onDismiss: () => void;
}

function FeedbackToast({ feedback, onDismiss }: FeedbackToastProps) {
  useEffect(() => {
    if (!feedback) return;

    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [feedback, onDismiss]);

  if (!feedback) return null;

  const config = {
    accepted: {
      icon: "check_circle",
      bg: "bg-green-50 border-green-200",
      text: "text-green-800",
      message: "Connection accepted! You're now connected.",
    },
    denied: {
      icon: "do_not_disturb_on",
      bg: "bg-gray-50 border-gray-200",
      text: "text-text-primary",
      message: "Request declined.",
    },
    cancelled: {
      icon: "block",
      bg: "bg-gray-50 border-gray-200",
      text: "text-text-primary",
      message: "Request cancelled.",
    },
  }[feedback.action];

  return (
    <div
      className={cn(
        "fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-xl border px-4 py-3 shadow-lg md:bottom-8",
        config.bg,
      )}
      role="status"
    >
      <Icon
        name={config.icon}
        filled
        className={cn("text-xl", config.text)}
      />
      <p className={cn("text-sm font-medium", config.text)}>
        {config.message}
      </p>
      <button
        onClick={onDismiss}
        className="ml-2 shrink-0 text-text-muted transition-colors hover:text-text-secondary"
        aria-label="Dismiss"
      >
        <Icon name="close" className="text-lg" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Sharing Requests Section
// ---------------------------------------------------------------------------

function SharingRequestsSection({
  requests,
  onConnectionUpdate,
}: {
  requests: PendingSharingRequest[];
  onConnectionUpdate: (updated: Connection) => void;
}) {
  const [grantTarget, setGrantTarget] = useState<{
    connectionId: number;
    field: SharableField;
    mode: SharingMode;
    days: number;
  } | null>(null);
  const [actioningKey, setActioningKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (requests.length === 0) return null;

  async function handleGrant() {
    if (!grantTarget) return;
    const { connectionId, field, mode, days } = grantTarget;
    const key = `grant-${connectionId}-${field}`;
    setActioningKey(key);
    setActionError(null);
    setGrantTarget(null);
    try {
      const updated = await grantDataSharing(connectionId, field, mode, days);
      onConnectionUpdate(updated);
    } catch {
      setActionError("Failed to grant access. Please try again.");
    } finally {
      setActioningKey(null);
    }
  }

  async function handleDecline(connectionId: number, field: SharableField) {
    const key = `decline-${connectionId}-${field}`;
    setActioningKey(key);
    setActionError(null);
    try {
      const updated = await revokeDataSharing(connectionId, field, "outbound");
      onConnectionUpdate(updated);
    } catch {
      setActionError("Failed to decline. Please try again.");
    } finally {
      setActioningKey(null);
    }
  }

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-base font-bold text-text-primary">Data Sharing Requests</h2>
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-white">
          {requests.length}
        </span>
      </div>

      <div className="space-y-2">
        {requests.map(({ connectionId, contactName, field }) => {
          const key = `grant-${connectionId}-${field}`;
          const declineKey = `decline-${connectionId}-${field}`;
          const isGranting = actioningKey === key;
          const isDeclining = actioningKey === declineKey;
          const isShowingGrant =
            grantTarget?.connectionId === connectionId && grantTarget.field === field;

          return (
            <div
              key={`${connectionId}-${field}`}
              className="rounded-2xl border border-[#ffdad9] bg-[#fff0f0] p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-text-primary">
                    {contactName} wants to see your{" "}
                    <span className="text-primary-600">{FIELD_LABELS[field]}</span>
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() =>
                      setGrantTarget({
                        connectionId,
                        field,
                        mode: field === "ssn" ? "show_valid" : "share",
                        days: 5,
                      })
                    }
                    disabled={isGranting || isShowingGrant}
                    className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-50 focus:outline-none"
                  >
                    {isGranting ? (
                      <Icon name="progress_activity" className="text-sm animate-spin" />
                    ) : (
                      "Grant"
                    )}
                  </button>
                  <button
                    onClick={() => handleDecline(connectionId, field)}
                    disabled={isDeclining}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-text-secondary transition-colors hover:border-red-200 hover:bg-red-50 hover:text-error disabled:opacity-50 focus:outline-none"
                  >
                    {isDeclining ? (
                      <Icon name="progress_activity" className="text-sm animate-spin" />
                    ) : (
                      "Decline"
                    )}
                  </button>
                </div>
              </div>

              {/* Inline grant config */}
              {isShowingGrant && grantTarget && (
                <div className="rounded-lg border border-primary-200 bg-white p-3 space-y-3">
                  {field !== "ssn" && (
                    <div className="flex gap-2">
                      {(["share", "show_valid"] as SharingMode[]).map((m) => (
                        <button
                          key={m}
                          onClick={() =>
                            setGrantTarget((t) => (t ? { ...t, mode: m } : null))
                          }
                          className={cn(
                            "flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none",
                            grantTarget.mode === m
                              ? "border-primary-500 bg-primary-500 text-white"
                              : "border-gray-200 bg-white text-text-secondary hover:border-primary-300",
                          )}
                        >
                          {m === "share" ? "Share Value" : "Show Verified Only"}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="text-xs text-text-secondary">
                      Expires in{" "}
                      <span className="font-semibold text-text-primary">
                        {grantTarget.days} days
                      </span>
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={30}
                      value={grantTarget.days}
                      onChange={(e) =>
                        setGrantTarget((t) =>
                          t ? { ...t, days: Number(e.target.value) } : null,
                        )
                      }
                      className="w-full accent-primary-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleGrant}
                      className="flex-1 rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-600 focus:outline-none"
                    >
                      Confirm Grant
                    </button>
                    <button
                      onClick={() => setGrantTarget(null)}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:bg-gray-50 focus:outline-none"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {actionError && (
        <p className="mt-2 text-xs text-error">{actionError}</p>
      )}

      <div className="mt-3 h-px bg-gray-100" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function RequestsPage() {
  const { user } = useAuth();
  const currentUserName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : undefined;
  const userFirstName = user?.first_name || "My";

  const [connections, setConnections] = useState<Connection[]>([]);
  const [verifiedConnections, setVerifiedConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedback>(null);

  // Retry counter
  const [fetchAttempt, setFetchAttempt] = useState(0);

  // ---- Fetch pending connections ----
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const data = await listConnections({
          status: "pending",
          sort: "-created",
          size: 200,
        });
        if (cancelled) return;

        // Read the dashboard's link cache to fill in link URLs without extra calls
        let linkCache: Connection[] = [];
        try {
          const stored = localStorage.getItem("uru_created_links");
          if (stored) linkCache = JSON.parse(stored) as Connection[];
        } catch { /* ignore */ }

        const merged = data.map((conn) =>
          linkCache.find((c) => c.id === conn.id) ?? conn,
        );

        // Fetch detail for any direct_link still missing a URL
        const enriched = await Promise.all(
          merged.map((conn) =>
            conn.source === "direct_link" && !conn.link
              ? getConnection(conn.id).catch(() => conn)
              : Promise.resolve(conn),
          ),
        );

        // Also fetch verified connections to surface pending data sharing requests
        const verified = await listConnections({
          status: "verified",
          size: 200,
        }).catch(() => [] as Connection[]);

        if (!cancelled) {
          setConnections(enriched);
          setVerifiedConnections(verified);
          setLoading(false);
          setError(null);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        let message = "Unable to load your pending requests.";
        if (err instanceof Error) {
          if (
            err.message.includes("network") ||
            err.message.includes("Network")
          ) {
            message =
              "Unable to reach the server. Please check your internet connection.";
          }
        }
        setError(message);
        setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [fetchAttempt]);

  // ---- Enriched and filtered requests ----
  const enrichedRequests = useMemo(
    () => connections.map((c) => enrichRequest(c, currentUserName)),
    [connections, currentUserName],
  );

  const filteredRequests = useMemo(() => {
    if (!searchQuery.trim()) return enrichedRequests;
    const q = searchQuery.toLowerCase();
    return enrichedRequests.filter((r) =>
      r.displayName.toLowerCase().includes(q),
    );
  }, [enrichedRequests, searchQuery]);

  // Separate inbound and outbound counts
  const inboundCount = enrichedRequests.filter((r) => !r.isOutbound).length;
  const outboundCount = enrichedRequests.filter((r) => r.isOutbound).length;

  // ---- Pending data sharing requests ----
  // Fields in my_sharing with status "pending" mean the other person is waiting for me to grant.
  const pendingSharingRequests = useMemo((): PendingSharingRequest[] => {
    const result: PendingSharingRequest[] = [];
    for (const conn of verifiedConnections) {
      if (!conn.my_sharing) continue;
      const person = conn.connected_customer;
      const contactName = person
        ? `${person.first_name} ${person.last_name}`.trim()
        : "Unknown";
      for (const [field, entry] of Object.entries(conn.my_sharing)) {
        if (entry?.status === "pending") {
          result.push({
            connectionId: conn.id,
            contactName,
            field: field as SharableField,
          });
        }
      }
    }
    return result;
  }, [verifiedConnections]);

  const handleSharingConnectionUpdate = useCallback((updated: Connection) => {
    setVerifiedConnections((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c)),
    );
  }, []);

  // ---- Action handlers ----
  const handleAccept = useCallback(
    async (id: number) => {
      setActioningId(id);
      try {
        await acceptConnection(id);

        // Auto-grant any pending sharing requests created by the requestor's field_settings.
        // Best-effort — don't block the accept flow if this fails.
        try {
          const detail = await getConnection(id);
          const pendingEntries = Object.entries(detail.my_sharing ?? {}).filter(
            ([, entry]) => entry?.status === "pending",
          );
          if (pendingEntries.length > 0) {
            await Promise.all(
              pendingEntries.map(([field, entry]) =>
                grantDataSharing(id, field as SharableField, entry?.mode ?? "show_valid"),
              ),
            );
          }
        } catch {
          // Auto-grant is best-effort; don't block the accept result
        }

        setConnections((prev) => prev.filter((c) => c.id !== id));
        setExpandedId(null);
        setFeedback({ connectionId: id, action: "accepted" });
      } catch {
        // Keep the item in the list on error
        setFeedback(null);
      }
      setActioningId(null);
    },
    [],
  );

  const handleDeny = useCallback(
    async (id: number, reason?: string) => {
      setActioningId(id);
      try {
        await denyConnection(id, reason);
        setConnections((prev) => prev.filter((c) => c.id !== id));
        setExpandedId(null);
        setFeedback({ connectionId: id, action: "denied" });
      } catch {
        setFeedback(null);
      }
      setActioningId(null);
    },
    [],
  );

  const handleCancel = useCallback(
    async (id: number) => {
      setActioningId(id);
      try {
        await cancelConnection(id);
        setConnections((prev) => prev.filter((c) => c.id !== id));
        setExpandedId(null);
        setFeedback({ connectionId: id, action: "cancelled" });
      } catch {
        setFeedback(null);
      }
      setActioningId(null);
    },
    [],
  );

  const handleRetry = useCallback(() => {
    setLoading(true);
    setError(null);
    setFetchAttempt((n) => n + 1);
  }, []);

  // ---- Render ----

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight text-text-primary">
            Requests
          </h1>
          {(enrichedRequests.length + pendingSharingRequests.length) > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#f08b8b] px-1.5 text-xs font-bold text-white">
              {enrichedRequests.length + pendingSharingRequests.length}
            </span>
          )}
        </div>
        <p className="mt-2 text-base text-text-secondary">
          {enrichedRequests.length === 0 && pendingSharingRequests.length === 0
            ? "Connection requests and data sharing requests will appear here."
            : `${inboundCount} inbound · ${outboundCount} sent by you`}
        </p>
      </div>

      {/* Error */}
      {error && <ErrorState message={error} onRetry={handleRetry} />}

      {/* Content */}
      {!error && (
        <>
          {/* Data sharing requests */}
          <SharingRequestsSection
            requests={pendingSharingRequests}
            onConnectionUpdate={handleSharingConnectionUpdate}
          />

          {/* Search bar — only show when there are connection requests */}
          {enrichedRequests.length > 0 && (
            <div className="relative mb-4">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="search" className="text-lg text-text-muted" />
              </div>
              <input
                type="text"
                placeholder="Search requests…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  "w-full rounded-2xl border-0 bg-white py-3 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:ring-2 focus:ring-[#f08b8b]/30 focus:outline-none",
                  CARD_SHADOW,
                )}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-muted transition-colors hover:text-text-secondary"
                  aria-label="Clear search"
                >
                  <Icon name="close" className="text-lg" />
                </button>
              )}
            </div>
          )}

          {/* Request list */}
          {filteredRequests.length === 0 ? (
            <EmptyState isSearching={!!searchQuery.trim()} />
          ) : (() => {
            const anonIndexMap = new Map<number, number>();
            [...connections]
              .filter((c) => !(c.label && !isPhoneLikeLabel(c.label)) && !c.connected_customer)
              .sort((a, b) => a.created - b.created)
              .forEach((c, i) => anonIndexMap.set(c.id, i + 1));

            return (
              <div className="space-y-3">
                {filteredRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    isExpanded={expandedId === request.id}
                    onToggle={() =>
                      setExpandedId((prev) =>
                        prev === request.id ? null : request.id,
                      )
                    }
                    onAccept={() => handleAccept(request.id)}
                    onDeny={(reason) => handleDeny(request.id, reason)}
                    onCancel={() => handleCancel(request.id)}
                    isActioning={actioningId === request.id}
                    userFirstName={userFirstName}
                    anonIndex={anonIndexMap.get(request.id)}
                  />
                ))}
              </div>
            );
          })()}

          {/* Informational footer */}
          {enrichedRequests.length > 0 && (
            <div className="mt-6 flex items-center justify-center gap-2 rounded-2xl bg-[#faf9ff] px-4 py-3 text-xs text-text-muted">
              <Icon name="info" className="shrink-0 text-base" />
              <p>
                Accepting a request creates a mutual connection — both parties
                appear in each other&apos;s verified contacts.
              </p>
            </div>
          )}
        </>
      )}

      {/* Feedback toast */}
      <FeedbackToast
        feedback={feedback}
        onDismiss={() => setFeedback(null)}
      />
    </div>
  );
}
